// Pre-defined permission "profiles" for the Authority Management page.
//
// The backend has no role concept beyond adminType (SUPER_ADMIN/SUB_ADMIN) —
// these presets are a purely front-end construct: named bundles of rules
// evaluated against whatever the live permission catalog currently contains,
// producing a concrete target set of grants. Applying a preset REPLACES the
// admin's grants to match that target set exactly (see diffPresetApplication).
import { moduleOf } from "./authorityLabels";

const keyOf = (entry) => `${entry.httpMethod} ${entry.pathTemplate}`;

function entryMatchesRule(entry, rule) {
  const moduleOk = rule.modules === "*" || rule.modules.includes(moduleOf(entry.pathTemplate));
  const methodOk = rule.methods === "*" || rule.methods.includes(entry.httpMethod);
  return moduleOk && methodOk;
}

export const PROFILE_PRESETS = [
  {
    id: "read_only",
    icon: "fa-eye",
    translationKey: "readOnly",
    rules: [{ modules: "*", methods: ["GET"] }],
  },
  {
    id: "event_manager",
    icon: "fa-calendar-check",
    translationKey: "eventManager",
    rules: [
      { modules: ["events", "tickets", "event-categories"], methods: "*" },
      { modules: "*", methods: ["GET"] },
    ],
  },
  {
    id: "club_coordinator",
    icon: "fa-people-group",
    translationKey: "clubCoordinator",
    rules: [
      { modules: ["clubs", "club-members", "club-categories"], methods: "*" },
      { modules: "*", methods: ["GET"] },
    ],
  },
  {
    id: "academic_structure_manager",
    icon: "fa-building-columns",
    translationKey: "academicStructureManager",
    rules: [
      { modules: ["campuses", "faculties", "departments"], methods: "*" },
      { modules: "*", methods: ["GET"] },
    ],
  },
  {
    id: "full_access",
    icon: "fa-key",
    translationKey: "fullAccess",
    rules: [{ modules: "*", methods: "*" }],
  },
];

export function computePresetGrantSet(preset, catalog) {
  return new Set(
    catalog.filter((entry) => preset.rules.some((rule) => entryMatchesRule(entry, rule))).map(keyOf)
  );
}

// Returns the id of the preset whose target set exactly matches the admin's
// current grants, or null if the current grants are "Custom" (match none).
export function detectActivePreset(presets, catalog, currentGrants) {
  if (catalog.length === 0) return null;
  const currentSet = new Set(currentGrants.map(keyOf));
  for (const preset of presets) {
    const target = computePresetGrantSet(preset, catalog);
    if (target.size === currentSet.size && [...target].every((key) => currentSet.has(key))) {
      return preset.id;
    }
  }
  return null;
}

// Computes what would need to change to make the admin's grants match the
// preset exactly: entries to grant (catalog entries) and entries to revoke
// (existing grant objects, so callers have adminUserId/httpMethod/pathTemplate).
export function diffPresetApplication(preset, catalog, currentGrants) {
  const target = computePresetGrantSet(preset, catalog);
  const currentSet = new Set(currentGrants.map(keyOf));
  const byKey = new Map(catalog.map((entry) => [keyOf(entry), entry]));

  const toGrant = [...target].filter((key) => !currentSet.has(key)).map((key) => byKey.get(key));
  const toRevoke = currentGrants.filter((grant) => !target.has(keyOf(grant)));

  return { toGrant, toRevoke };
}

export { keyOf };
